import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Home, Play, FileText, TrendingUp, LogOut,
  ChevronDown, Calendar, AlertTriangle, Wallet, GraduationCap, ClipboardCheck, BarChart3, Book,
  Brain, BookOpen, Award, Zap, HelpCircle, Video, UserPlus
} from 'lucide-react';

const ObserverNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [openDropdown, setOpenDropdown] = useState(null);
  
  const handleLogout = () => {
    localStorage.removeItem('observer_token');
    localStorage.removeItem('observer_user');
    navigate('/observer/login');
  };

  const navGroups = [
    { 
      id: 'home',
      label: 'Home', 
      icon: Home, 
      path: '/observer/dashboard',
      items: null
    },
    { 
      id: 'sessions',
      label: 'Sessions', 
      icon: Play,
      items: [
        { path: '/observer/today', label: 'Today', icon: Calendar },
        { path: '/observer/sessions', label: 'All Sessions', icon: Play },
        { path: '/observer/calls', label: 'Make a Call', icon: Zap },
        { path: '/observer/video-call', label: 'Video Call', icon: Video },
      ]
    },
    { 
      id: 'reports',
      label: 'Reports', 
      icon: FileText,
      items: [
        { path: '/observer/reports', label: 'My Reports', icon: FileText },
        { path: '/observer/call-analysis', label: 'Call Analysis', icon: Brain },
        { path: '/observer/escalations', label: 'Escalations', icon: AlertTriangle },
        { path: '/observer/audits', label: 'Audits', icon: ClipboardCheck },
      ]
    },
    { 
      id: 'coaching',
      label: 'AI Coach', 
      icon: Award,
      items: [
        { path: '/observer/coaching', label: 'Coaching Center', icon: Award },
        { path: '/observer/prep-notes', label: 'Prep Notes', icon: BookOpen },
      ]
    },
    { 
      id: 'growth',
      label: 'Growth', 
      icon: TrendingUp,
      items: [
        { path: '/observer/performance', label: 'Performance', icon: TrendingUp },
        { path: '/observer/quality-metrics', label: 'Quality Metrics', icon: BarChart3 },
        { path: '/observer/career', label: 'Career', icon: TrendingUp },
        { path: '/observer/training', label: 'Training', icon: GraduationCap },
        { path: '/observer/earnings', label: 'Earnings', icon: Wallet },
        { path: '/observer/payslips', label: 'Pay Slips', icon: FileText },
        { path: '/observer/withdrawal', label: 'Withdrawal', icon: Wallet },
      ]
    },
    { 
      id: 'resources',
      label: 'Resources', 
      icon: Book,
      items: [
        { path: '/observer/rulebook', label: 'Rule Book', icon: Book },
      ]
    },
    {
      id: 'refer',
      label: 'Refer Family',
      icon: UserPlus,
      path: '/observer/add-inquiry',
      items: null
    },
    {
      id: 'support',
      label: 'Support',
      icon: HelpCircle,
      path: '/observer/support',
      items: null
    },
  ];

  const isGroupActive = (group) => {
    if (group.path) return currentPath === group.path;
    return group.items?.some(item => currentPath === item.path || currentPath.startsWith(item.path + '/'));
  };

  return (
    <nav className="bg-white border-b shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_sanjaya-ai-1/artifacts/8tjvhfc4_Gemini_Generated_Image_8jnxm88jnxm88jnx.png" 
              alt="Sanjaya Logo" 
              className="w-8 h-8 rounded-lg object-contain"
            />
          </div>

          {/* Navigation - Desktop */}
          <div className="hidden md:flex items-center gap-1">
            {navGroups.map(group => {
              const Icon = group.icon;
              const isActive = isGroupActive(group);
              
              if (!group.items) {
                return (
                  <Button
                    key={group.id}
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => navigate(group.path)}
                    className={isActive
                      ? 'bg-violet-600 text-white hover:bg-violet-700 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {group.label}
                  </Button>
                );
              }
              
              return (
                <div key={group.id} className="relative">
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setOpenDropdown(openDropdown === group.id ? null : group.id)}
                    className={isActive
                      ? 'bg-violet-600 text-white hover:bg-violet-700 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                  >
                    <Icon className="w-4 h-4 mr-1.5" />
                    {group.label}
                    <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${openDropdown === group.id ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {openDropdown === group.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[180px] z-20">
                        {group.items.map(item => {
                          const ItemIcon = item.icon;
                          const isItemActive = currentPath === item.path;
                          return (
                            <button
                              key={item.path}
                              onClick={() => { navigate(item.path); setOpenDropdown(null); }}
                              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                                isItemActive
                                  ? 'bg-violet-100 text-violet-700 font-medium hover:bg-violet-200'
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4" />
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Logout */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Logout</span>
          </Button>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden overflow-x-auto pb-2 -mx-4 px-4">
          <div className="flex gap-2">
            {navGroups.map(group => {
              const Icon = group.icon;
              const isActive = isGroupActive(group);
              
              if (!group.items) {
                return (
                  <Button
                    key={group.id}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => navigate(group.path)}
                    className={`whitespace-nowrap ${isActive
                      ? 'bg-violet-600 text-white hover:bg-violet-700 hover:text-white'
                      : 'hover:bg-gray-100'}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {group.label}
                  </Button>
                );
              }
              
              return (
                <div key={group.id} className="relative">
                  <Button
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOpenDropdown(openDropdown === group.id ? null : group.id)}
                    className={`whitespace-nowrap ${isActive ? 'bg-violet-600' : ''}`}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {group.label}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                  
                  {openDropdown === group.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[130px] z-20">
                        {group.items.map(item => (
                          <button
                            key={item.path}
                            onClick={() => { navigate(item.path); setOpenDropdown(null); }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                              currentPath === item.path ? 'bg-violet-50 text-violet-600' : 'text-gray-700'
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default ObserverNav;
